import type { IOrganization, IUser } from "../../model/model";

const AUTH_USER_KEY = "wixar.profile.user";
const AUTH_ORGANIZATION_KEY = "wixar.profile.organization";
export const AUTH_JWT_KEY = "wixar.auth.jwt";
const AUTH_HELPDOCS_JWT_KEY = "wixar.auth.helpDocsJwt";
const AUTH_JWT_ISREMEMBERED = "wixar.auth.isRemebered";

export const storeInLocalStorage = (key: string, value: string | boolean) => {
  localStorage.setItem(key, String(value));
};

export const getFromLocalStorage = (key: string) => {
  return localStorage.getItem(key) ? localStorage.getItem(key) : "";
};

/**
 * Guarded Web Storage access for features that only use localStorage as a
 * convenience cache. Access throws in ordinary situations (site data blocked,
 * Safari private mode, quota exceeded on write) and none of them should break
 * the caller; the failure is reported to the console and a fallback returned.
 */
export const readLocalStorage = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch (err) {
    console.warn(`[localStorage] read failed for "${key}":`, err);
    return null;
  }
};

export const writeLocalStorage = (key: string, value: string): boolean => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (err) {
    console.warn(`[localStorage] write failed for "${key}":`, err);
    return false;
  }
};

export const removeLocalStorage = (key: string): boolean => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (err) {
    console.warn(`[localStorage] remove failed for "${key}":`, err);
    return false;
  }
};

export const storeAuthInLocalStorage = (
  user: IUser,
  organization: IOrganization,
  jwt: string,
  authHelpdocsJwt: string,
  isRemebered: boolean,
) =>
  new Promise((resolve, reject) => {
    try {
      storeInLocalStorage(AUTH_USER_KEY, JSON.stringify(user));
      storeInLocalStorage(AUTH_ORGANIZATION_KEY, JSON.stringify(organization));
      storeInLocalStorage(AUTH_JWT_KEY, jwt);
      storeInLocalStorage(AUTH_JWT_ISREMEMBERED, isRemebered);
      storeInLocalStorage(AUTH_HELPDOCS_JWT_KEY, authHelpdocsJwt);
      resolve(0);
    } catch (error) {
      reject(error instanceof Error ? error : new Error(String(error)));
    }
  });

export const patchStoredAuthUser = (patch: Partial<IUser>): void => {
  const userStr = readLocalStorage(AUTH_USER_KEY);
  if (!userStr) return;
  try {
    const user = JSON.parse(userStr) as IUser;
    writeLocalStorage(AUTH_USER_KEY, JSON.stringify({ ...user, ...patch }));
  } catch (err) {
    console.warn("[localStorage] patchStoredAuthUser failed:", err);
  }
};

export const getAuthFromLocalStorage = (): {
  user: IUser | null;
  organization: IOrganization | null;
  jwt: string | null;
  helpDocsJwt: string | null;
  isRemebered: boolean | null;
} => {
  const userStr = getFromLocalStorage(AUTH_USER_KEY);
  const user = userStr && userStr !== "" ? (JSON.parse(userStr) as IUser) : null;
  const orgStr = getFromLocalStorage(AUTH_ORGANIZATION_KEY);
  const organization = orgStr && orgStr !== "" ? (JSON.parse(orgStr) as IOrganization) : null;
  const jwt = getFromLocalStorage(AUTH_JWT_KEY);
  const helpDocsJwt = getFromLocalStorage(AUTH_HELPDOCS_JWT_KEY);
  const isRemeberedStr = getFromLocalStorage(AUTH_JWT_ISREMEMBERED);
  const isRemebered =
    isRemeberedStr && isRemeberedStr !== "" ? (JSON.parse(isRemeberedStr) as boolean) : null;
  return { user, organization, jwt, helpDocsJwt, isRemebered };
};
